'use client';

import React, { useState, useRef, useEffect, useId } from 'react';
import { Tag, Plus, X, Check, Minus } from 'lucide-react';
import { clsx } from 'clsx';
import { useFavorites } from './use-favorites';

interface MultiSelectLabelPopoverProps {
  selectedArticleNumbers: string[];
  onClose: () => void;
  /** Open the popover upward instead of downward (use when bar is fixed at bottom). */
  direction?: 'up' | 'down';
  /** Render trigger as a square icon-only button (h-9 w-9). */
  compact?: boolean;
  /** Style for the trigger button. `dark` fits on a dark (bg-overlay) selection bar. */
  variant?: 'light' | 'dark';
}

interface ToastMessage {
  text: string;
  key: number;
}

export function MultiSelectLabelPopover({
  selectedArticleNumbers,
  onClose,
  direction = 'down',
  compact = false,
  variant = 'light',
}: MultiSelectLabelPopoverProps) {
  const {
    userLabels,
    favorites,
    addUserLabel,
    assignLabel,
    removeLabel,
  } = useFavorites();
  const [open, setOpen] = useState(false);
  const [newLabelInput, setNewLabelInput] = useState('');
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Auto-hide toast after 2s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  /**
   * Determines how many of the selected articles have this label:
   * 'all' | 'some' | 'none'
   */
  const getLabelState = (label: string): 'all' | 'some' | 'none' => {
    const selectedFavs = favorites.filter((f) =>
      selectedArticleNumbers.includes(f.articleNumber)
    );
    const withLabel = selectedFavs.filter((f) =>
      (f.labels || []).includes(label)
    ).length;
    if (withLabel === 0) return 'none';
    if (withLabel === selectedFavs.length) return 'all';
    return 'some';
  };

  const handleToggleLabel = async (label: string) => {
    const state = getLabelState(label);
    if (state === 'all' || state === 'some') {
      // Remove from all selected
      await removeLabel(selectedArticleNumbers, label);
      showToast(
        `Kategorie '${label}' von ${selectedArticleNumbers.length} Artikel(n) entfernt`
      );
    } else {
      // Add to all selected
      await assignLabel(selectedArticleNumbers, label);
      showToast(
        `Kategorie '${label}' für ${selectedArticleNumbers.length} Artikel gesetzt`
      );
    }
  };

  const handleCreateLabel = async () => {
    const trimmed = newLabelInput.trim();
    if (!trimmed) return;
    await addUserLabel(trimmed);
    await assignLabel(selectedArticleNumbers, trimmed);
    showToast(`Kategorie '${trimmed}' erstellt & zugewiesen`);
    setNewLabelInput('');
    setOpen(false);
    onClose();
  };

  const showToast = (text: string) => {
    setToast({ text, key: Date.now() });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateLabel();
    }
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        className={clsx(
          'transition-colors border rounded-lg',
          variant === 'dark'
            ? open
              ? 'bg-white/20 text-white border-white/10'
              : 'bg-white/10 text-white border-white/10 hover:bg-white/20'
            : open
            ? 'bg-blue-100 text-blue-800 border-blue-200'
            : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50',
          compact
            ? 'h-9 w-9 inline-flex items-center justify-center'
            : variant === 'dark'
            ? 'px-3 py-2 text-sm font-bold inline-flex items-center justify-center gap-1.5'
            : 'px-3 py-2 text-sm font-medium flex items-center justify-center gap-1.5 min-h-[44px]'
        )}
        title="Kategorien für Auswahl vergeben"
        aria-label={compact ? 'Kategorien für Auswahl vergeben' : undefined}
      >
        <Tag size={compact ? 14 : 16} />
        {!compact && (
          <span className="hidden sm:inline">Kategorie vergeben</span>
        )}
      </button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Kategorie für Auswahl vergeben"
          className={clsx(
            'absolute w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150',
            direction === 'up'
              ? 'bottom-full mb-2 left-0'
              : 'top-full mt-2 left-0'
          )}
        >
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Kategorie für {selectedArticleNumbers.length} Artikel
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">
              Antippen zum Zuweisen oder Entfernen
            </p>
          </div>

          <div className="max-h-52 overflow-y-auto">
            {userLabels.length === 0 && (
              <p className="text-xs text-gray-400 px-3 py-3 italic">
                Noch keine Kategorien vorhanden.
              </p>
            )}
            {userLabels.map((label) => {
              const state = getLabelState(label);
              return (
                <button
                  key={label}
                  onClick={() => handleToggleLabel(label)}
                  className={clsx(
                    'w-full flex items-center justify-between px-3 py-3 text-sm text-left transition-colors min-h-[48px]',
                    state === 'all'
                      ? 'bg-primary/10 text-primary font-semibold hover:bg-red-50 hover:text-red-600'
                      : state === 'some'
                        ? 'bg-yellow-50 text-yellow-700 font-medium hover:bg-red-50 hover:text-red-600'
                        : 'hover:bg-gray-50 text-gray-700'
                  )}
                  title={
                    state === 'all'
                      ? `Kategorie '${label}' von allen entfernen`
                      : state === 'some'
                        ? `Kategorie '${label}' von allen entfernen`
                        : `Kategorie '${label}' allen zuweisen`
                  }
                >
                  <span className="truncate">{label}</span>
                  <span className="shrink-0 ml-2">
                    {state === 'all' && (
                      <X size={14} className="opacity-70" />
                    )}
                    {state === 'some' && (
                      <Minus size={14} className="opacity-70" />
                    )}
                    {state === 'none' && (
                      <Check
                        size={14}
                        className="opacity-0 group-hover:opacity-40"
                      />
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="border-t border-gray-100 p-2">
            <div className="flex gap-1">
              <input
                ref={inputRef}
                type="text"
                value={newLabelInput}
                onChange={(e) => setNewLabelInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Neue Kategorie..."
                maxLength={40}
                className="min-w-0 flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-gray-400"
              />
              <button
                onClick={handleCreateLabel}
                disabled={!newLabelInput.trim()}
                className="p-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0 min-w-[40px] min-h-[40px] flex items-center justify-center"
                title="Anlegen & zuweisen"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Feedback */}
      {toast && (
        <div
          key={toast.key}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-overlay text-white text-sm px-4 py-2.5 rounded-xl shadow-lg z-[100] animate-in fade-in slide-in-from-bottom-2 duration-200 whitespace-nowrap"
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
